// Registry of special screens apps add (schedule builder, check-in desk): they work over the same records and commands.
import type { ComponentType } from 'react';
import type { MenuSection } from '@confdeck/core-shared';
import type { NavActive } from './nav-tree.ts';

const screens = new Map<string, ComponentType>();
export function registerScreens(map: Record<string, ComponentType>) { for (const [k, c] of Object.entries(map)) screens.set(k, c); }
export const screenOf = (key: string) => screens.get(key);

/**
 * Tabs an app adds to the card of its type (stage B, sales Пр6): the core's tabs stay, the app's go before them
 * (a screen's «Сетка», an order's own views). Keyed by the type's key.
 */
/** `field`: the field the tab shows; without it in the section (a feature switched off, decision 025) the tab is not shown. */
/** `head` — not a tab: a block shown on the record's page above its tabs (the summary of a project, R-E8). */
/**
 * A tab of an app on a card. `field` — the tab is shown while the record's type has the field (or one of the fields) and the record's
 * words do not hide it (decision 025, R-E9).
 */
export interface RecordTab { key: string; label: string; component: ComponentType<{ recordId: string }>; field?: string | string[]; head?: boolean }
const recordTabs = new Map<string, RecordTab[]>();
export function registerRecordTabs(typeKey: string, tabs: RecordTab[]) { recordTabs.set(typeKey, [...(recordTabs.get(typeKey) ?? []), ...tabs]); }
export const recordTabsOf = (typeKey: string | undefined) => (typeKey ? recordTabs.get(typeKey) ?? [] : []);

/**
 * A notice an app shows above the table of a section (platform П10): «письма не уходят, почта не настроена».
 * Keyed by the type's key, like the record tabs; the notice reads the state itself.
 */
export interface TypeNotice { key: string; component: ComponentType }
const typeNotices = new Map<string, TypeNotice[]>();
export function registerTypeNotices(typeKey: string, notices: TypeNotice[]) { typeNotices.set(typeKey, [...(typeNotices.get(typeKey) ?? []), ...notices]); }
export const typeNoticesOf = (typeKey: string | undefined) => (typeKey ? typeNotices.get(typeKey) ?? [] : []);

/**
 * What an empty section shows instead of «пока пусто» (plan N1): an app offers its first step there — a new account's first
 * record made from a template. Shown only to a person who may write the section; keyed by the type's key.
 */
const typeEmpty = new Map<string, ComponentType>();
export function registerTypeEmpty(typeKey: string, component: ComponentType) { typeEmpty.set(typeKey, component); }
export const typeEmptyOf = (typeKey: string | undefined) => (typeKey ? typeEmpty.get(typeKey) : undefined);

/**
 * What a section shows in the left column instead of the plain tree (the mailboxes and folders of «Почта», R-M6). The column itself —
 * its tone, head, folding and drawer — stays the frame's (um/2026-10-30/03 п.1); the section fills only its list. `useTitle` names the
 * entity the person is on, for the head of the column; null — the frame names the open item of the tree.
 */
export interface SectionNav {
  nav: ComponentType<{ section: MenuSection; active: NavActive }>; useTitle: () => string | null;
  /** What stands at the bottom of the column — the slot of the event's plate in other sections (the mail's «Новое письмо»). */
  foot?: ComponentType;
}
const sectionNavs = new Map<string, SectionNav>();
export function registerSectionNav(sectionKey: string, nav: SectionNav) { sectionNavs.set(sectionKey, nav); }
export const sectionNavOf = (key: string | undefined) => (key ? sectionNavs.get(key) : undefined);

/** Where a record of a type opens instead of its page: a letter of the mail client opens in the client. Keyed by the type's key. */
const recordHomes = new Map<string, (id: string) => string>();
export function registerRecordHome(typeKey: string, href: (id: string) => string) { recordHomes.set(typeKey, href); }
export const recordHomeOf = (typeKey: string | undefined) => (typeKey ? recordHomes.get(typeKey) : undefined);
